/**
 * Client Auth Sync Component
 *
 * This component syncs the client-side Firebase Auth SDK with the server-side session.
 * It blocks rendering children until auth is synced to prevent race conditions with
 * Firestore subscriptions that require authenticated client.
 *
 * Uses reactfire's useSigninCheck for consistency with other components that check auth.
 *
 * It must be rendered inside both:
 * - AuthProvider (to access useAuth)
 * - CsrfTokenContext.Provider (to access CSRF token)
 */

import { useEffect, useRef, useState } from 'react';
import { useAuth, useSigninCheck } from 'reactfire';
import { signInWithCustomToken } from 'firebase/auth';

import { SessionRecoveryStartedError } from '~/core/firebase/client-auth-recovery';
import { useCsrf } from '~/core/firebase/hooks/use-csrf-token';
import logger from '~/core/logger';
import LogoImage from '~/core/ui/Logo/LogoImage';
import {
  readMissingSessionErrorResponse,
  readTerminalSessionErrorResponse,
  redirectToSignInForMissingSession,
  redirectToSignInForSessionRecovery,
} from '~/core/session/client-session-recovery';

/** Maximum time to wait for auth sync before rendering children in degraded mode */
const AUTH_SYNC_TIMEOUT_MS = 10_000;

export default function ClientAuthSync({
  children,
  serverUserId,
}: {
  children: React.ReactNode;
  serverUserId?: string | null;
}) {
  const auth = useAuth();
  const { token: csrfToken, refreshToken } = useCsrf();

  // Use reactfire's useSigninCheck for consistency with other hooks
  const { status: authStatus, data: signInCheck } = useSigninCheck();
  const isAuthReady = authStatus === 'success';
  const isSignedIn = isAuthReady && signInCheck?.signedIn;

  const [isSyncing, setIsSyncing] = useState(false);
  const [didSync, setDidSync] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const [recoveryStarted, setRecoveryStarted] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const syncAttemptedRef = useRef(false);
  const syncAttemptIdRef = useRef(0);
  const serverUserIdRef = useRef(serverUserId);

  const hasServerSession = !!serverUserId;

  useEffect(() => {
    serverUserIdRef.current = serverUserId;
    syncAttemptedRef.current = false;
    setIsSyncing(false);
    setDidSync(false);
    setSyncFailed(false);
    setRecoveryStarted(false);
    setIsTimedOut(false);
    syncAttemptIdRef.current++;
  }, [serverUserId]);

  // Timeout: if auth sync takes too long, render children in degraded mode
  // rather than showing a blank screen indefinitely
  useEffect(() => {
    if (
      !hasServerSession ||
      didSync ||
      syncFailed ||
      recoveryStarted ||
      isTimedOut
    ) {
      return;
    }

    const timer = setTimeout(() => {
      logger.warn(
        { serverUserId, isSyncing, didSync, syncFailed },
        'ClientAuthSync timed out after ' +
          AUTH_SYNC_TIMEOUT_MS +
          'ms, rendering in degraded mode',
      );
      setIsTimedOut(true);
    }, AUTH_SYNC_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [
    hasServerSession,
    didSync,
    syncFailed,
    recoveryStarted,
    isTimedOut,
    serverUserId,
    isSyncing,
  ]);

  // Sync client auth with server session
  // ALWAYS sync when there's a server session to ensure auth is fresh
  useEffect(() => {
    // Diagnostic: Log state on every render
    logger.info(
      {
        isAuthReady,
        isSignedIn,
        serverUserId: serverUserId || 'none',
        hasCsrfToken: !!csrfToken,
        isSyncing,
        didSync,
        syncFailed,
        syncAttempted: syncAttemptedRef.current,
      },
      'ClientAuthSync state check',
    );

    // Wait for auth check to complete
    if (!isAuthReady) {
      logger.info('Waiting for auth check to complete');
      return;
    }

    // Skip if already syncing, already synced, or sync failed
    if (isSyncing || didSync || syncFailed || recoveryStarted) {
      logger.info(
        { isSyncing, didSync, syncFailed, recoveryStarted },
        'Skipping sync',
      );
      return;
    }

    // Skip if no server session
    if (!serverUserId) {
      logger.info('No server session, skipping sync');
      return;
    }

    // Skip if we already attempted sync (prevents infinite retries)
    if (syncAttemptedRef.current) {
      logger.info('Sync already attempted, skipping');
      return;
    }

    // Skip if no CSRF token yet
    if (!csrfToken) {
      logger.info('No CSRF token yet, waiting');
      return;
    }

    // Mark that we're attempting sync
    syncAttemptedRef.current = true;
    const attemptId = ++syncAttemptIdRef.current;

    const syncAuth = async () => {
      const syncTarget = serverUserId;
      const isCurrentAttempt = () =>
        serverUserIdRef.current === syncTarget &&
        syncAttemptIdRef.current === attemptId;
      setIsSyncing(true);
      logger.info('Starting auth sync');

      // Helper to make the sync request
      const makeRequest = async (token: string): Promise<Response> => {
        return fetch('/api/session/sync-client-auth', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-csrf-token': token,
          },
        });
      };

      try {
        logger.info('Fetching custom token from server');

        let response = await makeRequest(csrfToken);

        if (await handleRecoveryResponse(response, isCurrentAttempt)) {
          if (isCurrentAttempt()) {
            setRecoveryStarted(true);
            setIsSyncing(false);
          }

          return;
        }

        // If CSRF validation failed, try refreshing token and retry
        if (response.status === 401) {
          logger.info('CSRF token may be stale, attempting refresh');
          const newToken = await refreshToken();
          if (newToken) {
            response = await makeRequest(newToken);

            if (await handleRecoveryResponse(response, isCurrentAttempt)) {
              if (isCurrentAttempt()) {
                setRecoveryStarted(true);
                setIsSyncing(false);
              }

              return;
            }
          }
        }

        if (!response.ok) {
          const text = await response.text();
          throw new Error(
            `Failed to get custom token: ${response.status} ${text}`,
          );
        }

        const { customToken } = await response.json();
        logger.info('Got custom token, signing in');

        // Sign in the client Auth SDK
        const signInResult = await signInWithCustomToken(auth, customToken);

        // CRITICAL: Verify the client uid matches the server uid
        const actualClientUid = auth.currentUser?.uid ?? signInResult.user?.uid;
        logger.info(
          {
            actualClientUid,
            serverUserId,
            match: actualClientUid === serverUserId,
          },
          'signInWithCustomToken completed - verifying uid',
        );

        if (actualClientUid !== serverUserId) {
          logger.error(
            { actualClientUid, serverUserId },
            'UID MISMATCH! Client auth uid does not match server session uid',
          );
          throw new Error('Client auth uid does not match server session uid');
        }

        if (!isCurrentAttempt()) {
          return;
        }

        // Mark sync complete AFTER signInWithCustomToken resolves
        // This ensures Firestore subscriptions use the fresh token
        setDidSync(true);
        setIsSyncing(false);
      } catch (err) {
        logger.error({ err }, 'Failed to sync client auth');
        if (!isCurrentAttempt()) {
          return;
        }
        if (err instanceof SessionRecoveryStartedError) {
          setRecoveryStarted(true);
          setIsSyncing(false);
          return;
        }
        setSyncFailed(true);
        setIsSyncing(false);
      }
    };

    syncAuth();
  }, [
    serverUserId,
    auth,
    csrfToken,
    refreshToken,
    isSyncing,
    didSync,
    syncFailed,
    recoveryStarted,
    isAuthReady,
    isSignedIn,
  ]);

  const hasMatchingClientAuth =
    isAuthReady && isSignedIn && auth.currentUser?.uid === serverUserId;

  // Block rendering only when a server session exists and we still need to sync.
  // Public pages should render immediately to avoid blank screens under load.
  // Note: We must wait for sync even if isSignedIn=true from persistence,
  // because the persisted token might be stale/expired.
  // Timeout degraded mode only renders when the hydrated client auth still
  // matches the server user; otherwise protected children remain blocked.
  const isReady = hasServerSession
    ? !recoveryStarted &&
      ((didSync && hasMatchingClientAuth) ||
        (syncFailed && hasMatchingClientAuth) ||
        (isTimedOut && hasMatchingClientAuth))
    : true;

  if (!isReady) {
    return (
      <div
        role="status"
        aria-label="Loading"
        className="flex min-h-screen flex-col items-center justify-center gap-5 bg-apple-bg"
      >
        <LogoImage className="w-44" />
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-apple-border border-t-apple-blue" />
      </div>
    );
  }

  return <>{children}</>;
}

async function handleRecoveryResponse(
  response: Response,
  isCurrentAttempt: () => boolean,
): Promise<boolean> {
  if (await readTerminalSessionErrorResponse(response)) {
    if (isCurrentAttempt()) {
      redirectToSignInForSessionRecovery();
    }

    return true;
  }

  if (await readMissingSessionErrorResponse(response)) {
    if (isCurrentAttempt()) {
      redirectToSignInForMissingSession();
    }

    return true;
  }

  return false;
}
